import { Controller } from "@hotwired/stimulus"
import { MediaFormMixin } from "./mixins/media_form_mixin"

export default class extends MediaFormMixin(Controller) {
  static targets = [
    // Partner searchable select
    "partnerDropdown", "partnerSelector", "partnerSelected", "partnerSearch", 
    "partnerList", "partnerHiddenField", "partnerEmptyState",
    // Matchmaker searchable select
    "matchmakerDropdown", "matchmakerSelector", "matchmakerSelected", "matchmakerSearch",
    "matchmakerList", "matchmakerHiddenField", "matchmakerEmptyState", "matchmakerWrapper",
    // Media upload
    "mediaInput", "mediaPreview", "mediaIdsContainer", "removeMediaIdsContainer"
  ]

  static values = {
    partnerId: String,
    matchmakerId: String,
    maxMedias: { type: Number, default: 3 },
    uploadUrl: { type: String, default: "/medias" }
  }

  connect() {
    this.friends = []
    this.mutualFriends = []
    this.selectedPartnerId = this.partnerIdValue || null
    this.selectedMatchmakerId = this.matchmakerIdValue || null
    this.searchTimeout = null
    this.debounceDelay = 200
    this.uploadedMediaIds = []
    this.removedMediaIds = []

    // Initialize partner and matchmaker display if editing existing testimonial
    if (this.selectedPartnerId && this.hasPartnerSelectedTarget) {
      // Partner name is already set in HTML, just update classes
      this.partnerSelectedTarget.classList.remove("text-gray-400")
      this.partnerSelectedTarget.classList.add("text-gray-900", "dark:text-white")
      
      // Fetch mutual friends if partner is selected
      this.fetchMutualFriends("")
    }

    if (this.selectedMatchmakerId && this.hasMatchmakerSelectedTarget) {
      // Matchmaker name is already set in HTML, just update classes
      this.matchmakerSelectedTarget.classList.remove("text-gray-400")
      this.matchmakerSelectedTarget.classList.add("text-gray-900", "dark:text-white")
    }

    // Close dropdowns on outside click
    this.closeOnOutsideClick = this.closeOnOutsideClick.bind(this)
    document.addEventListener("click", this.closeOnOutsideClick)

    this.setupWebcamListener()
  }

  handleWebcamCaptured(event) {
    const { file } = event.detail
    if (file) {
      this.uploadSingleMedia(file)
    }
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnOutsideClick)
    this.disconnectWebcamListener()
  }

  closeOnOutsideClick(event) {
    if (this.hasPartnerDropdownTarget && !this.partnerDropdownTarget.contains(event.target)) {
      this.closePartnerDropdown()
    }
    if (this.hasMatchmakerDropdownTarget && !this.matchmakerDropdownTarget.contains(event.target)) {
      this.closeMatchmakerDropdown()
    }
  }

  // ==================== PARTNER SELECTION ====================

  openPartnerDropdown() {
    if (!this.hasPartnerDropdownTarget) return
    this.partnerDropdownTarget.classList.remove("hidden")
    this.partnerSearchTarget.focus()
    if (this.friends.length === 0) {
      this.fetchFriends()
    } else {
      this.renderPartnerList(this.friends)
    }
  }

  closePartnerDropdown() {
    if (!this.hasPartnerDropdownTarget) return
    this.partnerDropdownTarget.classList.add("hidden")
    if (this.hasPartnerSearchTarget) {
      this.partnerSearchTarget.value = ""
    }
  }

  togglePartnerDropdown(event) {
    event.stopPropagation()
    if (this.partnerDropdownTarget.classList.contains("hidden")) {
      this.openPartnerDropdown()
    } else {
      this.closePartnerDropdown()
    }
  }

  searchPartner() {
    const query = this.partnerSearchTarget.value.trim()
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.fetchFriends(query)
    }, this.debounceDelay)
  }

  async fetchFriends(query = "") {
    try {
      const url = `/friends${query ? `?search=${encodeURIComponent(query)}` : ""}`
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error("Failed to fetch friends")

      this.friends = await response.json()
      this.renderPartnerList(this.friends)
    } catch (error) {
      console.error("[TestimonialForm] Error fetching friends:", error)
      this.friends = []
      this.renderPartnerList([])
    }
  }

  renderPartnerList(friends) {
    if (!this.hasPartnerListTarget) return

    if (friends.length === 0) {
      this.partnerListTarget.classList.add("hidden")
      if (this.hasPartnerEmptyStateTarget) {
        this.partnerEmptyStateTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasPartnerEmptyStateTarget) {
      this.partnerEmptyStateTarget.classList.add("hidden")
    }
    this.partnerListTarget.classList.remove("hidden")

    this.partnerListTarget.innerHTML = friends.map(friend => `
      <button type="button" 
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors text-left"
              data-action="click->testimonial-form#selectPartner"
              data-friend-id="${friend.id}"
              data-friend-name="${friend.display_name}">
        <img src="${friend.avatar_url || '/assets/default-avatar.png'}" 
             alt="${friend.display_name}" 
             class="w-8 h-8 rounded-full object-cover">
        <span class="text-sm text-gray-900 dark:text-white">${friend.display_name}</span>
      </button>
    `).join("")
  }

  selectPartner(event) {
    event.preventDefault()
    const friendId = event.currentTarget.dataset.friendId
    const friendName = event.currentTarget.dataset.friendName

    this.selectedPartnerId = friendId
    this.partnerHiddenFieldTarget.value = friendId
    this.partnerSelectedTarget.textContent = friendName
    this.partnerSelectedTarget.classList.remove("text-gray-400")
    this.partnerSelectedTarget.classList.add("text-gray-900", "dark:text-white")

    this.closePartnerDropdown()
    this.fetchMutualFriends("")
  }

  clearPartner(event) {
    event.stopPropagation()
    this.selectedPartnerId = null
    this.partnerHiddenFieldTarget.value = ""
    this.partnerSelectedTarget.textContent = this.partnerSelectedTarget.dataset.placeholder || "Sélectionner..."
    this.partnerSelectedTarget.classList.add("text-gray-400")
    this.partnerSelectedTarget.classList.remove("text-gray-900", "dark:text-white")
    
    this.clearMatchmaker()
    this.mutualFriends = []
    this.renderMatchmakerList([])
  }

  // ==================== MATCHMAKER SELECTION ====================

  openMatchmakerDropdown() {
    if (!this.hasMatchmakerDropdownTarget) return
    if (this.mutualFriends.length === 0) return
    
    this.matchmakerDropdownTarget.classList.remove("hidden")
    this.matchmakerSearchTarget.focus()
    this.renderMatchmakerList(this.mutualFriends)
  }

  closeMatchmakerDropdown() {
    if (!this.hasMatchmakerDropdownTarget) return
    this.matchmakerDropdownTarget.classList.add("hidden")
    if (this.hasMatchmakerSearchTarget) {
      this.matchmakerSearchTarget.value = ""
    }
  }

  toggleMatchmakerDropdown(event) {
    event.stopPropagation()
    if (this.mutualFriends.length === 0) return
    
    if (this.matchmakerDropdownTarget.classList.contains("hidden")) {
      this.openMatchmakerDropdown()
    } else {
      this.closeMatchmakerDropdown()
    }
  }

  searchMatchmaker() {
    const query = this.matchmakerSearchTarget.value.trim()
    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => {
      this.fetchMutualFriends(query)
    }, this.debounceDelay)
  }

  async fetchMutualFriends(searchQuery = "") {
    if (!this.selectedPartnerId) {
      this.mutualFriends = []
      this.renderMatchmakerList([])
      this.updateMatchmakerState()
      return
    }

    try {
      let url = `/mutual_friends?user_id=${this.selectedPartnerId}`
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`
      }
      
      const response = await fetch(url, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        credentials: "same-origin"
      })

      if (!response.ok) throw new Error("Failed to fetch mutual friends")

      this.mutualFriends = await response.json()
      this.renderMatchmakerList(this.mutualFriends)
      this.updateMatchmakerState()
    } catch (error) {
      console.error("[TestimonialForm] Error fetching mutual friends:", error)
      this.mutualFriends = []
      this.renderMatchmakerList([])
      this.updateMatchmakerState()
    }
  }

  renderMatchmakerList(friends) {
    if (!this.hasMatchmakerListTarget) return

    if (friends.length === 0) {
      this.matchmakerListTarget.classList.add("hidden")
      if (this.hasMatchmakerEmptyStateTarget) {
        this.matchmakerEmptyStateTarget.classList.remove("hidden")
      }
      return
    }

    if (this.hasMatchmakerEmptyStateTarget) {
      this.matchmakerEmptyStateTarget.classList.add("hidden")
    }
    this.matchmakerListTarget.classList.remove("hidden")

    this.matchmakerListTarget.innerHTML = friends.map(friend => `
      <button type="button" 
              class="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-zinc-700 transition-colors text-left"
              data-action="click->testimonial-form#selectMatchmaker"
              data-friend-id="${friend.id}"
              data-friend-name="${friend.display_name}">
        <img src="${friend.avatar_url || '/assets/default-avatar.png'}" 
             alt="${friend.display_name}" 
             class="w-8 h-8 rounded-full object-cover">
        <span class="text-sm text-gray-900 dark:text-white">${friend.display_name}</span>
      </button>
    `).join("")
  }

  selectMatchmaker(event) {
    event.preventDefault()
    const friendId = event.currentTarget.dataset.friendId
    const friendName = event.currentTarget.dataset.friendName

    this.selectedMatchmakerId = friendId
    this.matchmakerHiddenFieldTarget.value = friendId
    this.matchmakerSelectedTarget.textContent = friendName
    this.matchmakerSelectedTarget.classList.remove("text-gray-400")
    this.matchmakerSelectedTarget.classList.add("text-gray-900", "dark:text-white")

    this.closeMatchmakerDropdown()
  }

  clearMatchmaker(event) {
    if (event) event.stopPropagation()
    this.selectedMatchmakerId = null
    if (this.hasMatchmakerHiddenFieldTarget) {
      this.matchmakerHiddenFieldTarget.value = ""
    }
    if (this.hasMatchmakerSelectedTarget) {
      this.matchmakerSelectedTarget.textContent = this.matchmakerSelectedTarget.dataset.placeholder || "Sélectionner..."
      this.matchmakerSelectedTarget.classList.add("text-gray-400")
      this.matchmakerSelectedTarget.classList.remove("text-gray-900", "dark:text-white")
    }
  }

  // ==================== API CALLS ====================

  updateMatchmakerState() {
    if (!this.hasMatchmakerWrapperTarget) return

    if (this.mutualFriends.length === 0) {
      this.matchmakerWrapperTarget.classList.add("opacity-50", "pointer-events-none")
      if (this.hasMatchmakerSelectedTarget) {
        this.matchmakerSelectedTarget.textContent = "Aucun ami en commun"
      }
    } else {
      this.matchmakerWrapperTarget.classList.remove("opacity-50", "pointer-events-none")
      if (this.hasMatchmakerSelectedTarget && !this.selectedMatchmakerId) {
        this.matchmakerSelectedTarget.textContent = this.matchmakerSelectedTarget.dataset.placeholder || "Sélectionner..."
      }
    }
  }

  // ==================== MEDIA UPLOAD ====================

  async uploadMedia(event) {
    const files = event.target.files
    if (!files || files.length === 0) return

    const remainingSlots = this.maxMediasValue - this.uploadedMediaIds.length
    if (remainingSlots <= 0) {
      alert(`Maximum ${this.maxMediasValue} médias autorisés`)
      return
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    for (const file of filesToUpload) {
      await this.uploadSingleMedia(file)
    }

    // Clear input
    event.target.value = ""
  }

  async uploadSingleMedia(file) {
    try {
      const media = await this.uploadMediaFile(file, this.uploadUrlValue)
      if (media) {
        this.uploadedMediaIds.push(media.id)
        this.addMediaPreview({
          id: media.id,
          url: media.original_url,
          thumb_url: media.thumb_url || media.original_url,
          category: media.category
        })
        this.updateMediaIdsInput()
      }
    } catch (error) {
      console.error("[TestimonialForm] Error uploading media:", error)
    }
  }

  addMediaPreview(media) {
    if (!this.hasMediaPreviewTarget) return

    const div = document.createElement("div")
    div.className = "relative group flex-none w-24 h-24 mb-3"
    div.dataset.mediaId = media.id

    const isVideo = media.category === "video"
    
    if (isVideo) {
      div.innerHTML = `
        <video src="${media.url}" class="w-full h-full object-cover rounded-lg" muted></video>
        <div class="absolute inset-0 flex items-center justify-center bg-black/50 rounded pointer-events-none">
          <svg class="w-3 h-3 inline" fill="currentColor" viewBox="0 0 20 20">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
          </svg>
        </div>
      `
    } else {
      div.innerHTML = `
        <img src="${media.thumb_url || media.url}" class="w-full h-full object-cover rounded-lg" alt="">
      `
    }

    // Add remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "absolute top-1 right-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full p-1 transition-colors z-10"
    removeBtn.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `
    removeBtn.addEventListener("click", () => this.removeMedia(media.id, div))
    div.appendChild(removeBtn)

    this.mediaPreviewTarget.appendChild(div)
    this.mediaPreviewTarget.classList.remove("hidden")
  }

  removeMedia(mediaId, element) {
    this.uploadedMediaIds = this.uploadedMediaIds.filter(id => id !== mediaId)
    element.remove()
    this.updateMediaIdsInput()
    if (this.mediaPreviewTarget.children.length === 0) {
      this.mediaPreviewTarget.classList.add("hidden")
    }
  }

  updateMediaIdsInput() {
    if (!this.hasMediaIdsContainerTarget) return

    this.mediaIdsContainerTarget.innerHTML = this.uploadedMediaIds.map(id => 
      `<input type="hidden" name="media_ids[]" value="${id}">`
    ).join("")
  }

  removeExistingMedia(event) {
    const mediaId = event.currentTarget.dataset.mediaId
    const mediaElement = event.currentTarget.closest("[data-media-id]")

    if (mediaElement) {
      mediaElement.remove()
    }

    // Add to removed media IDs
    this.removedMediaIds.push(mediaId)
    this.updateRemoveMediaIdsInput()

    if (this.mediaPreviewTarget.children.length === 0) {
      this.mediaPreviewTarget.classList.add("hidden")
    }
  }

  updateRemoveMediaIdsInput() {
    if (!this.hasRemoveMediaIdsContainerTarget) return

    this.removeMediaIdsContainerTarget.innerHTML = this.removedMediaIds.map(id => 
      `<input type="hidden" name="remove_media_ids[]" value="${id}">`
    ).join("")
  }
}
